/**
 * Cutting, Bending & Edge Punching Services Order Schema v2.0 — payload builder.
 *
 * Pure, framework-free mapper. Converts the configurator's local state (desired
 * cut pieces, bend rows, generated pieces, pricing) into the single `services`
 * object the backend now expects at POST /Cart/addoption and /Quotes/addoption:
 *
 *   { cartId, qty, services: ServiceOrderPayload }
 *
 * v2.0 changes from v1.1:
 *   - Everything is wrapped in `order` (with id / customerId / createdAt).
 *   - Explicit `shape` ("sheet" | "round" | "roll") on the stock and every piece
 *     (no more inferring sheet-vs-linear by field presence).
 *   - `stockDimensions` → `dimensions`; round cross-section in `dimensions.diameter`;
 *     stock-level `thickness` and `profile` removed.
 *   - Cuts drop tolerance / cuttingMethod / edgeFinish. Bends drop angleTolerance
 *     and position.referenceFrame; bend `radius` is optional.
 *   - Bends carry a top-level `distance` (cumulative from the reference edge); the
 *     reference edge is shared across the piece via `Piece.bendFromEdge`
 *     ('length' | 'width' for sheets, 'length' only for round), set only when the
 *     piece has bends. (Replaces the old per-bend `orientation` + `position` block.)
 *   - New `edgePunching` boolean, allowed only on `roll` pieces.
 *
 * v2.1: a Channel Raceway cover is sent as its OWN output piece (bendPreset
 *   'channel-raceway-cover', its own bends), cut from the stock, placed directly
 *   before the channel piece it caps. Its bends bill with the rest.
 *
 * The backend stores the payload verbatim and validates billing math, structure,
 * schemaVersion and the disposition/edgePunching rules, so this module guarantees
 * exactness for all of them.
 */

// ──────────────────────────────────────────────────────────────────────────
// Output schema (v2.0)
// ──────────────────────────────────────────────────────────────────────────

export type Disposition = 'return' | 'discard' | 'recycle';
export type BendPreset = 'Z' | 'U' | 'J' | 'L' | 'Siding' | 'Molding' | 'ChannelRaceway' | 'ChannelRacewayCover';
export const COVER_BEND_PRESET: BendPreset = 'ChannelRacewayCover';

/** The presets the backend accepts on a piece (2.1). The app keeps its own names
 *  (`BendPreset`); they are translated here, at the payload boundary only. */
export type WireBendPreset = 'Z' | 'U' | 'J' | 'L' | 'siding' | 'molding' | 'channel-raceway' | 'channel-raceway-cover';

const PRESET_TO_WIRE: Partial<Record<BendPreset, WireBendPreset>> = {
  Z: 'Z', U: 'U', J: 'J', L: 'L',
  Siding: 'siding',
  Molding: 'molding',
  ChannelRaceway: 'channel-raceway',
  ChannelRacewayCover: 'channel-raceway-cover',
};

/** App preset -> backend value; null for anything the backend doesn't list. */
export function presetToWire(preset: BendPreset | null | undefined): WireBendPreset | null {
  return (preset && PRESET_TO_WIRE[preset]) || null;
}

/** Backend value (or an older stored app name) -> app preset. */
export function presetFromWire(v: any): BendPreset | null {
  if (!v) return null;
  if (v === 'siding') return 'Siding';
  if (v === 'molding') return 'Molding';
  if (v === 'channel-raceway') return 'ChannelRaceway';
  if (v === 'channel-raceway-cover') return 'ChannelRacewayCover';
  return v as BendPreset;
}
export type Direction = 'up' | 'down';
export type Orientation = 'parallel-to-width' | 'parallel-to-length';
export type RefEdge = 'width' | 'length';
export type RefPoint = 'start' | 'end';
export type Shape = 'sheet' | 'round' | 'roll';

export interface Dimensions {
  width?: number;
  length: number;
  diameter?: number;
}

export interface Stock {
  /** Informational only — the backend identifies the product from the cart line. */
  productSlug: string;
  material: string;
  quantity: number;
  shape: Shape;
  dimensions: Dimensions;
}

export interface SheetCutLine {
  referenceEdge: RefEdge;
  referencePoint: RefPoint;
  distance: number;
  orientation: Orientation;
}
export interface LinearCutLine {
  referencePoint: RefPoint;
  distance: number;
}

export interface Cut {
  id: string;
  sequence: number;
  sourcePieceId: string;
  line: SheetCutLine | LinearCutLine;
  produces: [string, string];
}

export interface Bend {
  id: string;
  sequence: number;
  angle: number;
  radius?: number;
  direction: Direction;
  /** Cumulative distance from the start of the reference edge to this bend.
   *  The reference edge itself is shared across the piece via `Piece.bendFromEdge`. */
  distance: number;
}

export interface Piece {
  id: string;
  label: string;
  role: 'source' | 'intermediate' | 'output';
  shape: Shape;
  dimensions: Dimensions;
  quantity: number;
  bends: Bend[];
  /** Which edge all of this piece's bend distances are measured from. One shared
   *  value for the whole piece (sheet: 'length' | 'width'; round: 'length' only).
   *  Set only when the piece has bends. */
  bendFromEdge?: RefEdge;
  customerDisposition?: Disposition;
  bendPreset?: WireBendPreset;
  outsideMaterial?: string;
  notes?: string;
  edgePunching?: boolean; // roll pieces only
}

export interface Attachment {
  id: string;
  filename: string;
  url: string;
  scope: 'cutting' | 'bending' | 'general';
  uploadedAt: string;
}

export interface ServiceLine {
  basePrice: number;
  includedQuantity: number;
  additionalUnitPrice: number;
  quantity: number;
  total: number;
}

export interface Billing {
  currency: string;
  services: Record<string, ServiceLine>;
  subtotal: number;
  tax?: { rate: number; total: number };
  total: number;
}

export interface OrderBody {
  id: string;
  customerId: string;
  createdAt: string;
  notes?: string;
  stock: Stock;
  cuts: Cut[];
  pieces: Piece[];
  attachments?: Attachment[];
  billing: Billing;
}

export interface ServiceOrderPayload {
  schemaVersion: '2.1';
  unitSystem: 'imperial' | 'metric';
  order: OrderBody;
}

// ──────────────────────────────────────────────────────────────────────────
// Input contract (what callers assemble from their local state)
// ──────────────────────────────────────────────────────────────────────────

export interface ProductContext {
  /** Informational product reference (slug is fine). */
  productSlug: string;
  /** Per-unit material price → material billing line. */
  unitPrice: number;
  material?: string | null;
  /** sheet | round | roll. Defaults to sheet when unknown. */
  shape?: Shape | null;
  width?: number | null;
  length?: number | null;
  diameter?: number | null;
  /** Finish id applied to bent pieces (e.g. 'red-shiny'). */
  outsideMaterial?: string | null;
}

export type ServiceKind = 'cutting' | 'bending' | 'punching' | 'slitting';

export interface PricingInput {
  basePrice: number;
  includedQuantity: number;
  additionalUnitPrice: number;
}

export interface MeasurementInput {
  width?: number | null;
  length?: number | null;
  qty?: number | null;
}

export interface BendRowInput {
  distance?: number | null;
  direction?: Direction;
  angle?: number | null;
  useCustom?: boolean;
  customAngle?: number | null;
}

/** A Channel Raceway cover: its own bend rows plus the closing flange after the
 *  last bend (null -> mirror the first flange). */
export interface CoverInput {
  bendRows: BendRowInput[];
  endFlange?: number | null;
}

export interface PieceInput {
  label?: string;
  width?: number | null;
  length?: number | null;
  qty?: number | null;
  bendRows?: BendRowInput[];
  bendPreset?: BendPreset | null;
  /** Which edge the bend distances run along: 'length' (default) or 'width'. */
  bendAxis?: 'length' | 'width';
  cover?: CoverInput | null;
}

export interface UploadedFileInput {
  name?: string;
  url?: string;
}

export interface ServiceInput {
  kind: ServiceKind;
  pricing: PricingInput;
  measurements?: MeasurementInput[];
  pieces?: PieceInput[];
  bendRows?: BendRowInput[];
  bendPreset?: BendPreset | null;
  /** Standalone (bend-only) sheet: which edge the bend distances run along. */
  bendAxis?: 'length' | 'width';
  /** Standalone (bend-only) Channel Raceway cover. */
  cover?: CoverInput | null;
  notes?: string;
  attachment?: UploadedFileInput | null;
}

export interface ServiceOrderInput {
  product: ProductContext;
  /** Stock quantity (the cart-line qty). */
  qty: number;
  notes?: string;
  services: ServiceInput[];
  attachment?: UploadedFileInput | null;
  /** ISO timestamp for order.createdAt and attachments. */
  now?: string;
  /** order.id — pass the cart-line id. */
  orderId?: string;
  /** order.customerId. */
  customerId?: string;
  /** When true, the leftover material is returned to the customer
   *  (customerDisposition: 'return'); otherwise discarded as scrap (default). */
  includeRemainingMaterial?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Defaults
// ──────────────────────────────────────────────────────────────────────────

const EPS = 0.001;
const DEFAULT_BEND_ANGLE = 90;
const FALLBACK_SHEET = { width: 48, length: 120 };
const FALLBACK_LINEAR_LENGTH = 120;
const FALLBACK_DIAMETER = 1;

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

/** Round to 2 decimals, robust against float error. */
export function round2(n: number): number {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function round3(n: number): number {
  return Math.round((Number(n) + Number.EPSILON) * 1000) / 1000;
}

function num(v: any, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function pos(v: any, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Parse a numeric / decimal / fraction string into a number. Null if invalid. */
export function parseMeasurementValue(raw: any): number | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^(\d+)\s+(\d+)\/(\d+)$/))) {
    const den = +m[3];
    return den ? +m[1] + +m[2] / den : null;
  }
  if ((m = s.match(/^(\d+)\/(\d+)$/))) {
    const den = +m[2];
    return den ? +m[1] / den : null;
  }
  if (/^(\d+(\.\d+)?|\.\d+)$/.test(s)) {
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  }
  return null;
}

/** Normalize a measurement string to inches. (') or ft/feet = feet ×12;
 *  (")/('') or in/inch = inches; no unit ⇒ inches; handles combined feet+inches
 *  like 8'6". Null if unparseable. */
export function parseLengthToInches(raw: any): number | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^(.+?)'\s*(.+?)(?:''|")$/))) {
    const ft = parseMeasurementValue(m[1]);
    const inch = parseMeasurementValue(m[2]);
    return ft === null || inch === null ? null : ft * 12 + inch;
  }
  // Word units: "5 FT", "10 feet", "24 in", "3 inches".
  if ((m = s.match(/^(.+?)\s*(?:ft|feet|foot)$/i))) {
    const ft = parseMeasurementValue(m[1]);
    return ft === null ? null : ft * 12;
  }
  if ((m = s.match(/^(.+?)\s*(?:in|inch|inches)$/i))) {
    return parseMeasurementValue(m[1]);
  }
  if (s.endsWith('"') || s.endsWith("''")) {
    return parseMeasurementValue(s.replace(/(''|")\s*$/, ''));
  }
  if (s.endsWith("'")) {
    const ft = parseMeasurementValue(s.slice(0, -1));
    return ft === null ? null : ft * 12;
  }
  return parseMeasurementValue(s);
}

/** Read a product's attributesDTOs entry by name and normalize it to inches. */
export function productAttrInches(product: any, name: string): number | null {
  const attrs = product?.attributesDTOs;
  if (!Array.isArray(attrs)) return null;
  const attr = attrs.find((a: any) => a?.attributeName?.toLowerCase() === name.toLowerCase());
  return parseLengthToInches(attr?.attributeValue);
}

/** Read a product's attributesDTOs entry by name as a trimmed string. */
export function productAttrString(product: any, name: string): string | null {
  const attrs = product?.attributesDTOs;
  if (!Array.isArray(attrs)) return null;
  const attr = attrs.find((a: any) => a?.attributeName?.toLowerCase() === name.toLowerCase());
  const raw = attr?.attributeValue;
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  return s === '' ? null : s;
}

/** Infer the stock shape from product attributes. Defaults to 'sheet'. */
export function determineShape(product: any): Shape {
  const explicit = (
    productAttrString(product, 'shape') ||
    productAttrString(product, 'form') ||
    productAttrString(product, 'profile') ||
    productAttrString(product, 'type') ||
    product?.shape ||
    ''
  )
    .toString()
    .toLowerCase();
  if (explicit.includes('roll')) return 'roll';
  if (explicit.includes('round') || explicit.includes('rod')) return 'round';
  if (explicit.includes('sheet')) return 'sheet';
  if (productAttrInches(product, 'diameter') != null) return 'round';
  return 'sheet';
}

/** Build a ProductContext from a raw product object. */
export function buildProductContext(product: any): ProductContext {
  return {
    productSlug: product?.urlSlug || product?.partName || 'unknown',
    unitPrice: Number(product?.price ?? product?.salePrice ?? 0) || 0,
    material: productAttrString(product, 'material') || product?.material || null,
    shape: determineShape(product),
    width: productAttrInches(product, 'width'),
    length: productAttrInches(product, 'length'),
    diameter: productAttrInches(product, 'diameter'),
    outsideMaterial: product?.outsideMaterial || null,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-builders
// ──────────────────────────────────────────────────────────────────────────

function isRound(shape: Shape): boolean {
  return shape === 'round';
}

/** Build a dimensions object for the given shape. */
function makeDims(shape: Shape, width: number | null, length: number, diameter: number | null): Dimensions {
  if (isRound(shape)) {
    return { length: Math.max(EPS, length), diameter: Math.max(EPS, diameter ?? FALLBACK_DIAMETER) };
  }
  return { width: Math.max(EPS, width ?? 0), length: Math.max(EPS, length) };
}

function buildStock(product: ProductContext, qty: number, shape: Shape): Stock {
  const material = (product.material && String(product.material).trim()) || 'unknown';
  const dimensions: Dimensions = isRound(shape)
    ? { length: pos(product.length, FALLBACK_LINEAR_LENGTH), diameter: pos(product.diameter, FALLBACK_DIAMETER) }
    : { width: pos(product.width, FALLBACK_SHEET.width), length: pos(product.length, FALLBACK_SHEET.length) };
  return {
    productSlug: product.productSlug || 'unknown',
    material,
    quantity: qty,
    shape,
    dimensions,
  };
}

/** The edge all of a piece's bend distances are measured from, shared across the
 *  whole piece. Round pieces only ever have a single edge → 'length'; sheet pieces
 *  honor the user's Bend Side choice (`bendAxis`). */
function bendFromEdgeFor(shape: Shape, bendAxis: 'length' | 'width' = 'length'): RefEdge {
  return isRound(shape) ? 'length' : bendAxis === 'width' ? 'width' : 'length';
}

/** Build the bends array for a single piece from its bend rows. Each bend carries a
 *  top-level cumulative `distance`; the reference edge is shared across the piece via
 *  `Piece.bendFromEdge` (see `bendFromEdgeFor`). */
function buildBends(rows: BendRowInput[] | undefined): Bend[] {
  if (!rows || !rows.length) return [];
  let cumulative = 0;
  return rows.map((row, i) => {
    const seg = num(row.distance, 0);
    cumulative += seg;
    const angle = row.useCustom ? num(row.customAngle, num(row.angle, DEFAULT_BEND_ANGLE)) : num(row.angle, DEFAULT_BEND_ANGLE);
    return {
      id: `bend-${i + 1}`,
      sequence: i + 1,
      angle,
      direction: row.direction === 'down' ? 'down' : 'up',
      distance: Math.max(EPS, cumulative),
    };
  });
}

/** The flat width a cover needs: every flange, closing one included. */
function coverFlatWidth(cover: CoverInput): number {
  const segs = cover.bendRows.map((r) => num(r.distance, 0));
  const closing = num(cover.endFlange, 0) || segs[0] || 0;
  return round3(segs.reduce((s, d) => s + d, 0) + closing);
}

/** Usable cover (sheet only, with at least one bend and a positive flat width). */
function usableCover(cover: CoverInput | null | undefined, shape: Shape): CoverInput | null {
  if (!cover || isRound(shape) || !cover.bendRows?.length) return null;
  return coverFlatWidth(cover) > 0 ? cover : null;
}

function applyBends(piece: Piece, rows: BendRowInput[] | undefined, preset: BendPreset | null | undefined,
  shape: Shape, bendAxis: 'length' | 'width' | undefined, product: ProductContext) {
  const bends = buildBends(rows);
  if (!bends.length) return;
  piece.bends = bends;
  piece.bendFromEdge = bendFromEdgeFor(shape, bendAxis);
  const wire = presetToWire(preset);
  if (wire) piece.bendPreset = wire;
  if (product.outsideMaterial) piece.outsideMaterial = String(product.outsideMaterial);
}

interface CutSpec {
  width: number | null;
  length: number;
  qty: number;
  bendRows?: BendRowInput[];
  bendPreset?: BendPreset | null;
  bendAxis?: 'length' | 'width';
  cover?: CoverInput | null;
  /** Set only on the synthetic unit that carries a raceway cover. */
  isCover?: boolean;
}

/**
 * Synthesize cuts[] + pieces[] FAITHFULLY: each measurement the user enters
 * becomes exactly one finished output piece with the EXACT width × length × qty
 * they typed. Pieces are produced as a simple chain off the source; each cut
 * splits the running remainder into [requested piece, remainder]; the final
 * remainder is scrap. cuts.length == Σ qty.
 */
function buildCutChain(
  source: Piece,
  specs: CutSpec[],
  shape: Shape,
  product: ProductContext,
  includeRemaining: boolean,
): { cuts: Cut[]; pieces: Piece[] } {
  const cuts: Cut[] = [];
  const pieces: Piece[] = [source];
  const sheetLike = !isRound(shape);
  const SW = sheetLike ? source.dimensions.width ?? 0 : 0;
  const diameter = isRound(shape) ? source.dimensions.diameter ?? FALLBACK_DIAMETER : null;
  let cutSeq = 0;
  let pid = 0;
  let coverIdx = 0;

  const newPiece = (role: Piece['role'], dimensions: Dimensions): Piece => {
    const p: Piece = { id: `p-${++pid}`, label: '', role, shape, dimensions, quantity: 1, bends: [] };
    pieces.push(p);
    return p;
  };

  const emitCut = (from: Piece, distance: number, a: Piece, b: Piece) => {
    cuts.push({
      id: `cut-${++cutSeq}`,
      sequence: cutSeq,
      sourcePieceId: from.id,
      line: sheetLike
        ? { referenceEdge: 'length', referencePoint: 'start', distance: round3(distance), orientation: 'parallel-to-width' }
        : { referencePoint: 'start', distance: round3(distance) },
      produces: [a.id, b.id],
    });
  };

  // Expand each measurement into individual finished units (qty copies), keeping
  // the EXACT entered width and length.
  // A raceway cover is one more unit off the same chain, directly before its
  // channel: same run along the bend lines, with the cover's own flat width across
  // the bend axis (bends across the width -> cover is coverW x l; across the
  // length -> w x coverW).
  interface Unit { w: number | null; l: number; spec: CutSpec; }
  const units: Unit[] = [];
  for (const spec of specs) {
    const l = num(spec.length, 0);
    if (l <= 0) continue;
    const w = sheetLike ? (spec.width && spec.width > 0 ? spec.width : SW) : null;
    const qty = Math.max(1, Math.round(num(spec.qty, 1)));
    const cover = spec.bendRows?.length ? usableCover(spec.cover, shape) : null;
    const coverSpec: CutSpec | null = cover
      ? { width: null, length: 0, qty: 1, bendRows: cover.bendRows, bendPreset: COVER_BEND_PRESET, bendAxis: spec.bendAxis, isCover: true }
      : null;
    const cw = cover ? coverFlatWidth(cover) : 0;
    for (let k = 0; k < qty; k++) {
      if (coverSpec) units.push(spec.bendAxis === 'width' ? { w: cw, l, spec: coverSpec } : { w, l: cw, spec: coverSpec });
      units.push({ w, l, spec });
    }
  }
  if (!units.length) return { cuts, pieces };

  let current = source;
  let remLen = source.dimensions.length;

  units.forEach((u, i) => {
    const isLast = i === units.length - 1;

    const piece = newPiece('output', makeDims(shape, u.w, u.l, diameter));
    piece.customerDisposition = 'return';
    if (u.spec.isCover) piece.label = `Raceway cover ${++coverIdx}`;
    applyBends(piece, u.spec.bendRows, u.spec.bendPreset, shape, u.spec.bendAxis, product);

    remLen = Math.max(EPS, remLen - u.l);
    const second = isLast
      ? newPiece('output', makeDims(shape, sheetLike ? SW : null, remLen, diameter))
      : newPiece('intermediate', makeDims(shape, sheetLike ? SW : null, remLen, diameter));
    // The final remainder is the leftover material: returned to the customer when
    // "Include Remaining Material" is on, otherwise discarded as scrap.
    if (isLast) {
      second.customerDisposition = includeRemaining ? 'return' : 'discard';
      if (includeRemaining) second.label = 'Remaining Material';
    }

    emitCut(current, u.l, piece, second);
    current = second;
  });

  // Labels.
  let outIdx = 0;
  let scrapIdx = 0;
  for (const p of pieces) {
    if (p.label) continue;
    if (p.role === 'source') p.label = 'Original stock';
    else if (p.role === 'intermediate') p.label = 'Intermediate';
    else if (p.customerDisposition === 'discard') p.label = `Scrap ${++scrapIdx}`;
    else p.label = `Piece ${++outIdx}`;
  }

  return { cuts, pieces };
}

// ──────────────────────────────────────────────────────────────────────────
// Reading a stored order back (edit / remove-one-service round trips)
// ──────────────────────────────────────────────────────────────────────────

export function isCoverPiece(p: any): boolean {
  return presetFromWire(p?.bendPreset) === COVER_BEND_PRESET;
}

/** A stored piece's bends as per-segment rows (the payload stores cumulative
 *  distances; older payloads kept them under `position.distance`). */
export function bendRowsFromPiece(p: any): { distance: number; direction: Direction; angle: number }[] {
  const bends = (p?.bends || []).slice().sort((a: any, b: any) => a.sequence - b.sequence);
  let prev = 0;
  return bends.map((b: any) => {
    const cumulative = Number(b.distance ?? b.position?.distance) || 0;
    const distance = cumulative - prev;
    prev = cumulative;
    return { distance, direction: b.direction === 'down' ? 'down' : 'up', angle: b.angle ?? DEFAULT_BEND_ANGLE };
  });
}

/** Rebuild a CoverInput from a stored cover piece: the closing flange is whatever
 *  of its flat width (across the bend axis) is left after the last bend. */
export function coverInputFromPiece(p: any): CoverInput {
  const bendRows = bendRowsFromPiece(p);
  const flat = Number(p?.bendFromEdge === 'width' ? p?.dimensions?.width : p?.dimensions?.length) || 0;
  const used = bendRows.reduce((s, r) => s + r.distance, 0);
  const endFlange = round3(flat - used);
  return { bendRows, endFlange: endFlange > 0 ? endFlange : null };
}

/** Bent pieces of a stored order with each raceway cover folded onto the channel
 *  that follows it (the builder emits a cover directly before its channel). */
export function bentPiecesWithCovers(pieces: any[]): { piece: any; cover: CoverInput | null }[] {
  const out: { piece: any; cover: CoverInput | null }[] = [];
  let pending: CoverInput | null = null;
  for (const p of pieces || []) {
    if (!(p?.bends?.length > 0)) continue;
    if (isCoverPiece(p)) { pending = coverInputFromPiece(p); continue; }
    out.push({ piece: p, cover: presetFromWire(p.bendPreset) === 'ChannelRaceway' ? pending : null });
    pending = null;
  }
  return out;
}

function buildAttachments(input: ServiceOrderInput): Attachment[] {
  const out: Attachment[] = [];
  const now = input.now || '';
  let idx = 0;
  const push = (file: UploadedFileInput | null | undefined, scope: Attachment['scope']) => {
    if (file && file.url) {
      out.push({ id: `att-${++idx}`, filename: file.name || 'attachment', url: file.url, scope, uploadedAt: now });
    }
  };
  input.services.forEach((s) => {
    if (s.attachment && s.attachment.url) {
      push(s.attachment, s.kind === 'bending' ? 'bending' : s.kind === 'cutting' || s.kind === 'slitting' ? 'cutting' : 'general');
    }
  });
  push(input.attachment, 'general');
  return out;
}

function serviceLine(pricing: PricingInput, quantity: number): ServiceLine {
  const basePrice = round2(num(pricing.basePrice, 0));
  const includedQuantity = Math.max(0, Math.round(num(pricing.includedQuantity, 0)));
  const additionalUnitPrice = round2(num(pricing.additionalUnitPrice, 0));
  const total = round2(basePrice + Math.max(0, quantity - includedQuantity) * additionalUnitPrice);
  return { basePrice, includedQuantity, additionalUnitPrice, quantity, total };
}

// ──────────────────────────────────────────────────────────────────────────
// Structural validation (dev guard — never ship an invalid payload)
// ──────────────────────────────────────────────────────────────────────────

export function validateStructure(payload: ServiceOrderPayload): string[] {
  const errors: string[] = [];
  if (payload.schemaVersion !== '2.1') errors.push('schemaVersion must be "2.1".');
  const order = payload.order;
  if (!order) {
    errors.push('Missing order.');
    return errors;
  }
  const { stock, cuts, pieces, billing } = order;

  const ids = pieces.map((p) => p.id);
  if (new Set(ids).size !== ids.length) errors.push('Piece ids are not unique.');

  const sources = pieces.filter((p) => p.role === 'source');
  if (sources.length !== 1) errors.push(`Expected exactly one source piece, found ${sources.length}.`);
  else {
    const s = sources[0];
    if (s.quantity !== stock.quantity) errors.push('Source piece quantity must equal stock quantity.');
    if (s.shape !== stock.shape) errors.push('Source piece shape must equal stock shape.');
  }

  const idSet = new Set(ids);
  const cutIds = cuts.map((c) => c.id);
  if (new Set(cutIds).size !== cutIds.length) errors.push('Cut ids are not unique.');
  cuts.forEach((c, i) => {
    if (c.sequence !== i + 1) errors.push(`Cut sequence not contiguous at index ${i}.`);
    if (!idSet.has(c.sourcePieceId)) errors.push(`Cut ${c.id} references missing sourcePieceId ${c.sourcePieceId}.`);
    c.produces.forEach((pid) => {
      if (!idSet.has(pid)) errors.push(`Cut ${c.id} produces missing piece ${pid}.`);
    });
  });

  const cutsEmpty = cuts.length === 0;
  pieces.forEach((p) => {
    if (p.role === 'output' && !p.customerDisposition) errors.push(`Output piece ${p.id} missing customerDisposition.`);
    if (p.role === 'intermediate' && p.customerDisposition) errors.push(`Intermediate piece ${p.id} must not have customerDisposition.`);
    if (p.role === 'source' && p.customerDisposition && !cutsEmpty) {
      errors.push(`Source piece ${p.id} may only have customerDisposition when cuts is empty.`);
    }
    if (p.bendPreset && p.bends.length === 0) errors.push(`Piece ${p.id} has bendPreset but no bends.`);
    if (p.bendFromEdge && p.bends.length === 0) errors.push(`Piece ${p.id} has bendFromEdge but no bends.`);
    if (p.bendFromEdge === 'width' && p.shape === 'round') errors.push(`Round piece ${p.id} may only bend from the length edge.`);
    if (p.edgePunching === true && p.shape !== 'roll') errors.push(`edgePunching is allowed only on roll pieces (piece ${p.id}).`);
    p.bends.forEach((b, i) => {
      if (b.sequence !== i + 1) errors.push(`Bend sequence not contiguous on piece ${p.id} at index ${i}.`);
    });
  });

  // Billing consistency.
  const sumLines = round2(Object.values(billing.services).reduce((s, l) => s + l.total, 0));
  if (sumLines !== round2(billing.subtotal)) errors.push('subtotal does not equal the sum of service line totals.');
  Object.entries(billing.services).forEach(([key, l]) => {
    const expected = round2(l.basePrice + Math.max(0, l.quantity - l.includedQuantity) * l.additionalUnitPrice);
    if (expected !== round2(l.total)) errors.push(`Billing line "${key}" total is inconsistent.`);
  });
  const expectedTotal = round2(billing.subtotal + (billing.tax ? billing.tax.total : 0));
  if (expectedTotal !== round2(billing.total)) errors.push('total does not equal subtotal + tax.');
  if (billing.services['material'] && billing.services['material'].quantity !== stock.quantity) {
    errors.push('material.quantity must equal stock.quantity.');
  }

  return errors;
}

// ──────────────────────────────────────────────────────────────────────────
// Entry point
// ──────────────────────────────────────────────────────────────────────────

export function buildServicesPayloadV11(input: ServiceOrderInput): ServiceOrderPayload {
  const product = input.product;
  const qty = Math.max(1, Math.round(num(input.qty, 1)));
  // Return the leftover material to the customer only when the caller opts in;
  // otherwise it is discarded as scrap (the long-standing default).
  const includeRemaining = input.includeRemainingMaterial === true;

  // Edge punching is a roll-only / vinyl service (it's only ever offered for roll
  // stock), so its presence is authoritative about the stock shape. Product shape
  // detection can misclassify a vinyl roll as a sheet (e.g. a 15" × 10yd roll with
  // no explicit "roll" attribute); when punching is selected we must treat the
  // stock as a roll, otherwise the backend rejects edgePunching on a sheet piece
  // and the punch is silently dropped — no edgePunching flag and no billing line.
  const punchingSvc = input.services.find((s) => s.kind === 'punching');
  const shape: Shape = punchingSvc ? 'roll' : product.shape || 'sheet';
  const sheetLike = !isRound(shape);

  const stock = buildStock(product, qty, shape);

  const source: Piece = {
    id: 'p-src',
    label: 'Original stock',
    role: 'source',
    shape,
    dimensions: { ...stock.dimensions },
    quantity: qty,
    bends: [],
  };

  // Cut-producing service: cutting (sheet/round) or slitting (roll). Bends from
  // the bending service. Edge punching from the punching service (roll only).
  const cutSvc = input.services.find(
    (s) => (s.kind === 'cutting' || s.kind === 'slitting') && (s.measurements?.length || 0) > 0,
  );
  const bendingSvc = input.services.find((s) => s.kind === 'bending');

  const measurements = (cutSvc?.measurements || []).filter((m) => num(m.length, 0) > 0 && num(m.qty, 0) > 0);
  const bendingPieces = bendingSvc?.pieces || [];

  const specs: CutSpec[] = measurements.map((m, i) => {
    const bp = bendingPieces[i];
    const rows = bp?.bendRows && bp.bendRows.length ? bp.bendRows : undefined;
    const preset = bp?.bendPreset ?? bendingSvc?.bendPreset ?? null;
    return {
      width: num(m.width, 0) || null,
      length: num(m.length, 0),
      qty: Math.max(1, Math.round(num(m.qty, 1))),
      bendRows: rows,
      bendPreset: rows ? preset : null,
      bendAxis: bp?.bendAxis === 'width' ? 'width' : 'length',
      cover: rows && preset === 'ChannelRaceway' ? bp?.cover ?? null : null,
    };
  });

  let cuts: Cut[] = [];
  let pieces: Piece[] = [source];
  let bendingQuantity = 0;

  if (specs.length > 0) {
    const chain = buildCutChain(source, specs, shape, product, includeRemaining);
    cuts = chain.cuts;
    pieces = chain.pieces;
    bendingQuantity = pieces.reduce((s, p) => s + p.bends.length * p.quantity, 0);
  } else if (bendingSvc && bendingSvc.bendRows && bendingSvc.bendRows.length) {
    const cover = bendingSvc.bendPreset === 'ChannelRaceway' ? usableCover(bendingSvc.cover, shape) : null;
    if (cover) {
      // Bend-only raceway with a cover: one cut takes the cover's flat width off the
      // stock across the bend axis (a rip when bending across the width, as in the
      // backend's 2.1 sample); the rest is the channel.
      const cw = coverFlatWidth(cover);
      const acrossWidth = bendingSvc.bendAxis === 'width';
      const SW = source.dimensions.width ?? 0;
      const SL = source.dimensions.length;
      const coverPiece: Piece = {
        id: 'p-cover', label: 'Raceway cover', role: 'output', shape, quantity: qty, bends: [],
        dimensions: acrossWidth ? { width: cw, length: SL } : { width: SW, length: cw },
        customerDisposition: 'return',
      };
      const body: Piece = {
        id: 'p-body', label: 'Raceway body', role: 'output', shape, quantity: qty, bends: [],
        dimensions: acrossWidth
          ? { width: Math.max(EPS, round3(SW - cw)), length: SL }
          : { width: SW, length: Math.max(EPS, round3(SL - cw)) },
        customerDisposition: 'return',
      };
      applyBends(coverPiece, cover.bendRows, COVER_BEND_PRESET, shape, bendingSvc.bendAxis, product);
      applyBends(body, bendingSvc.bendRows, bendingSvc.bendPreset, shape, bendingSvc.bendAxis, product);
      cuts = [{
        id: 'cut-1',
        sequence: 1,
        sourcePieceId: source.id,
        line: acrossWidth
          ? { referenceEdge: 'width', referencePoint: 'start', distance: cw, orientation: 'parallel-to-length' }
          : { referenceEdge: 'length', referencePoint: 'start', distance: cw, orientation: 'parallel-to-width' },
        produces: [coverPiece.id, body.id],
      }];
      pieces = [source, coverPiece, body];
      bendingQuantity = (coverPiece.bends.length + body.bends.length) * qty;
    } else {
      // Bend-only: the source piece itself carries the bends and is the deliverable.
      applyBends(source, bendingSvc.bendRows, bendingSvc.bendPreset, shape, bendingSvc.bendAxis, product);
      bendingQuantity = source.bends.length * source.quantity;
    }
  }

  // Edge punching (roll only).
  let punchedCount = 0;
  if (punchingSvc && shape === 'roll') {
    const targets = cuts.length > 0 ? pieces.filter((p) => p.role === 'output' && p.customerDisposition === 'return') : [source];
    targets.forEach((p) => {
      p.edgePunching = true;
      punchedCount += p.quantity;
    });
  }

  // When there are no cuts, the source is the deliverable → it carries a disposition.
  if (cuts.length === 0) {
    source.customerDisposition = 'return';
  }

  // ── Billing ──
  const services: Record<string, ServiceLine> = {};
  services['material'] = serviceLine(
    { basePrice: 0, includedQuantity: 0, additionalUnitPrice: round2(num(product.unitPrice, 0)) },
    qty,
  );
  // A bend-only raceway cover still needs one cut; it bills only when a cutting
  // service (with pricing) was chosen.
  const cutBillSvc = cutSvc || input.services.find((s) => s.kind === 'cutting');
  if (cutBillSvc && cuts.length > 0) {
    services[cutBillSvc.kind] = serviceLine(cutBillSvc.pricing, cuts.length);
  }
  if (bendingSvc && bendingQuantity > 0) {
    services['bending'] = serviceLine(bendingSvc.pricing, bendingQuantity);
  }
  if (punchingSvc && punchedCount > 0) {
    services['punching'] = serviceLine(punchingSvc.pricing, punchedCount);
  }

  const subtotal = round2(Object.values(services).reduce((s, l) => s + l.total, 0));
  const billing: Billing = {
    currency: 'USD',
    services,
    subtotal,
    total: subtotal, // tax omitted at add-to-cart; computed later at checkout
  };

  const order: OrderBody = {
    id: input.orderId || 'cart-line',
    customerId: input.customerId || 'web-customer',
    createdAt: input.now || '',
    stock,
    cuts,
    pieces,
    billing,
  };
  if (input.notes && String(input.notes).trim()) order.notes = String(input.notes).trim().slice(0, 500);

  const attachments = buildAttachments(input);
  if (attachments.length) order.attachments = attachments;

  const payload: ServiceOrderPayload = {
    schemaVersion: '2.1',
    unitSystem: 'imperial',
    order,
  };

  const errors = validateStructure(payload);
  if (errors.length) {
    throw new Error('Invalid v2.1 services payload:\n' + errors.join('\n'));
  }

  return payload;
}
